<template>
    <div class="med-sync-enrollment">
        <div v-if="loading" class="text-center py-5">
            <b-spinner label="Loading..."></b-spinner>
            <div class="mt-2">Loading eligible prescriptions...</div>
        </div>

        <template v-else>
            <b-row>
                <b-col>
                    <p class="text-muted">
                        MedSync allows patients to pick up all their eligible medications on the same day each cycle,
                        improving medication adherence and reducing multiple pharmacy visits.
                    </p>
                </b-col>
            </b-row>

            <hr>

            <!-- Anchor Date Configuration -->
            <b-row class="mb-3">
                <b-col md="6">
                    <b-form-group
                            label="Anchor Date"
                            label-for="anchor-date"
                            description="The synchronized fill date for all selected medications">
                        <el-date-picker
                                id="anchor-date"
                                v-model="anchorDate"
                                type="date"
                                placeholder="Select anchor date"
                                format="MM/dd/yyyy"
                                value-format="yyyy-MM-dd"
                                :picker-options="anchorDatePickerOptions"
                                style="width: 100%;">
                        </el-date-picker>
                    </b-form-group>
                </b-col>

                <b-col md="6">
                    <b-form-group
                            label="Cycle Days"
                            label-for="cycle-days"
                            description="Frequency (in days) for synchronized medication pickups">
                        <b-form-input
                                id="cycle-days"
                                v-model="cycleDays"
                                type="number"
                                min="1"
                                max="365"
                                placeholder="Enter cycle days">
                        </b-form-input>
                        <small class="text-muted">Common values: 30 (monthly), 90 (quarterly)</small>
                    </b-form-group>
                </b-col>
            </b-row>

            <!-- Cycle Compatibility Warning -->
            <b-row v-if="showCycleCompatibilityWarning" class="mb-3">
                <b-col>
                    <b-alert variant="warning" show class="mb-0">
                        <i class="fa fa-exclamation-triangle"></i>
                        <strong>Warning:</strong> Some selected medications have day supplies that may not align well with the {{ cycleDays }}-day cycle.
                        These prescriptions may require short fills or quantity adjustments to maintain synchronization.
                        <div v-if="incompatiblePrescriptions.length > 0" class="mt-2">
                            <small>
                                <strong>Incompatible medications:</strong>
                                <ul class="mb-0 mt-1">
                                    <li v-for="rx in incompatiblePrescriptions" :key="rx.rxID">
                                        {{ rx.drugName }} ({{ rx.daySupply }} days)
                                    </li>
                                </ul>
                            </small>
                        </div>
                    </b-alert>
                </b-col>
            </b-row>

            <hr>

            <!-- Eligible Medications Selection -->
            <b-row class="mb-3">
                <b-col>
                    <div class="d-flex justify-content-between align-items-center mb-2">
                        <div>
                            <h5 class="mb-1">Select Medications for MedSync</h5>
                            <p class="text-muted mb-0">
                                Select at least two medications to include in the MedSync program.
                                Only medications eligible for AutoRefill are shown.
                            </p>
                        </div>
                    </div>

                    <div v-if="eligiblePrescriptions.length === 0" class="text-center text-muted py-4">
                        <i class="fa fa-info-circle fa-2x mb-2"></i>
                        <p>No medications are currently eligible for MedSync enrollment.</p>
                    </div>

                    <b-table v-else
                            :items="eligiblePrescriptions"
                            :fields="medicationFields"
                            hover
                            class="mb-0">

                        <template #cell(selected)="data">
                            <b-form-checkbox v-model="selectedPrescriptions" :value="data.item.rxID"/>
                        </template>

                        <template #cell(medication)="data">
                            <div>
                                <strong>{{ data.item.rxID }}</strong><br>
                                {{ data.item.drugName }}
                            </div>
                        </template>

                        <template #cell(daySupply)="data">
                            {{ data.item.daySupply ? `${ data.item.daySupply } days` : 'N/A' }}
                        </template>

                        <template #cell(nextFillDate)="data">
                            {{ formatDate(data.item.nextFillDate) }}
                        </template>

                        <template #cell(refillsRemaining)="data">
                            {{ data.item.refillsRemaining !== null ? data.item.refillsRemaining : 'N/A' }}
                        </template>
                    </b-table>

                    <b-row v-if="eligiblePrescriptions.length > 0" align-v="baseline">
                        <b-col cols="auto">
                            <b-button
                                v-if="eligiblePrescriptions.length > 0"
                                variant="outline-primary"
                                size="sm"
                                @click="toggleSelectAll">
                                {{ allSelected ? 'Deselect All' : 'Select All' }}
                            </b-button>
                        </b-col>
                        <b-col cols="auto">
                            <small class="text-info">
                                <i class="fa fa-info-circle"></i>
                                {{ selectedPrescriptions.length }} of {{ eligiblePrescriptions.length }} medications selected
                            </small>
                        </b-col>
                    </b-row>
                </b-col>
            </b-row>

            <hr>

            <!-- Short Fill Processing Option -->
            <b-row class="mb-3">
                <b-col>
                    <b-form-group>
                        <b-form-checkbox v-model="enableShortFill" size="lg">
                            <strong>Automatically process short fill prescriptions</strong>
                        </b-form-checkbox>
                        <small class="text-muted d-block ml-4">
                            When enabled, the system will calculate appropriate Days Supply adjustments to synchronize
                            all selected medications to the anchor date without medication gaps. Short fills will be
                            automatically tagged with the appropriate SCC (Submission Clarification Code) for billing.
                        </small>
                    </b-form-group>
                </b-col>
            </b-row>

            <hr>

            <!-- Payment Card Selection -->
            <b-row class="mb-3">
                <b-col>
                    <h5>Payment Method</h5>
                    <p class="text-muted">
                        Select the card that will be charged for the MedSync refill subscriptions:
                    </p>
                    <StripeVueCard :patient="patient" @cardAdded="cardAdded"></StripeVueCard>
                    <CardsTable
                            ref="cardsTable"
                            v-model="patient.stripeCustomerId"
                            :selectedCardID="selectedCardId"
                            @cardSelected="setCardID">
                    </CardsTable>
                </b-col>
            </b-row>

            <hr>
            <b-alert v-if="errorMessage" variant="danger" show dismissible @dismissed="errorMessage = ''">
                {{ errorMessage }}
            </b-alert>
            <!-- Action Buttons -->
            <b-row>
                <b-col class="text-right">
                    <b-button variant="secondary" @click="cancel" class="mr-2">
                        Cancel
                    </b-button>
                    <b-button
                            variant="primary"
                            @click="save"
                            :disabled="!isValid || saving">
                        <b-spinner v-if="saving" small class="mr-1"></b-spinner>
                        {{ saving ? 'Enrolling...' : 'Enroll in MedSync' }}
                    </b-button>
                </b-col>
            </b-row>
        </template>

        <!-- Refill in Batch Modal for Short Fills -->
        <b-modal
                ref="refillBatchModal"
                id="medsync-refill-batch-modal"
                size="xl"
                title="Process Short Fill Prescriptions"
                hide-footer>
            <RefillInBatchModal
                    v-if="showRefillBatchModal"
                    :patient="patient"
                    :prescriptions="shortFillPrescriptions"
                    :anchorDate="anchorDate"
                    @completed="onShortFillCompleted" />
        </b-modal>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Patient } from '@/models/Patient';
import { Prescription } from '@/models/Prescription';
import { StripeCard } from '@/models/Payment';
import { EligiblePrescription } from '@/models/MedSync';
import { DatePicker } from 'element-ui';
import axios from 'axios';
import moment from 'moment';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import RefillInBatchModal from './RefillInBatchModal.vue';
import CardsTable from '@/components/Stripe/CardsTable.vue';
import StripeVueCard from '@/components/Payments/StripeVueCardcomponent.vue';

@Component({
    components: {
        [DatePicker.name]: DatePicker, RefillInBatchModal, CardsTable, StripeVueCard,
    },
})
export default class MedSyncEnrollmentModal extends Vue {
    @Prop({ required: true }) patient!: Patient;
    @Prop({ default: () => [] }) prescriptions!: Prescription[];

    // Data properties
    private loading = true;
    private saving = false;
    private errorMessage = '';

    // Configuration properties
    private anchorDate: Date | null = null;
    private cycleDays: number | null = null;
    private selectedPrescriptions: string[] = [];
    private enableShortFill = false;
    private selectedCardId: string | null = null;
    private allowAutorefillWithoutPayment = false;

    // Prescription data
    private eligiblePrescriptions: EligiblePrescription[] = [];
    protected shortFillPrescriptions: any[] = [];
    private showRefillBatchModal = false;

    // Table fields configuration
    private medicationFields = [
        { key: 'selected', label: '', tdClass: 'align-middle', thStyle: { width: '50px' } },
        { key: 'medication', label: 'Medication', tdClass: 'align-middle' },
        { key: 'daySupply', label: 'Days Supply', tdClass: 'align-middle' },
        { key: 'nextFillDate', label: 'Next Fill Date', tdClass: 'align-middle' },
        { key: 'refillsRemaining', label: 'Refills', tdClass: 'align-middle' },
    ];

    // Date picker options
    get anchorDatePickerOptions() {
        return {
            disabledDate(date: Date) {
                // Disable past dates
                return date < new Date(new Date().setHours(0, 0, 0, 0));
            },
        };
    }

    async mounted() {
        await this.loadEligiblePrescriptions();
        await this.getAllowAutorefillWithoutPaymentFromConfig();

        // Load patient payment methods after component is fully rendered
        await this.$nextTick();
        this.reloadCards();
    }

    async getAllowAutorefillWithoutPaymentFromConfig() {
        try {
            const response = await axios.get<boolean>('/configuration/AllowAutorefillWithoutPayment');
            this.allowAutorefillWithoutPayment = response.data;
        } catch (error) {
            console.error('Error loading AllowAutorefillWithoutPayment config:', error);
            this.allowAutorefillWithoutPayment = false;
        }
    }

    setCardID(cardID: string) {
        this.selectedCardId = cardID;
    }

    cardAdded(card: StripeCard) {
        this.patient.stripeCustomerId = card.customerId;
        this.reloadCards();
    }

    reloadCards() {
        const cardsTable = this.$refs.cardsTable as any;
        if (cardsTable && typeof cardsTable.loadPatientPaymentMethods === 'function') {
            cardsTable.loadPatientPaymentMethods();
        }
    }

    async loadEligiblePrescriptions() {
        this.loading = true;
        try {
            // Get prescriptions eligible for MedSync with enhanced data (days supply, next fill date, refills)
            const response = await axios.get<EligiblePrescription[]>(`/api/medsync/eligible-prescriptions/${ this.patient.id }`);
            this.eligiblePrescriptions = response.data;

            // Set default values
            this.setDefaultAnchorDate();
            this.setDefaultCycleDays();
        } catch (error) {
            console.error('Error loading eligible prescriptions:', error);
            this.errorMessage = 'Failed to load eligible prescriptions. Please try again.';
        } finally {
            this.loading = false;
        }
    }

    setDefaultAnchorDate() {
        if (this.eligiblePrescriptions.length === 0) return;

        // Find the latest next fill date among eligible prescriptions
        const latestDate = this.eligiblePrescriptions.reduce((latest, rx) => {
            if (!rx.nextFillDate) return latest;
            const rxDate = new Date(rx.nextFillDate);
            return rxDate > latest ? rxDate : latest;
        }, new Date(this.eligiblePrescriptions[0].nextFillDate || new Date()));

        this.anchorDate = latestDate;
    }

    setDefaultCycleDays() {
        if (this.eligiblePrescriptions.length === 0) return;

        // Find the longest days supply among eligible prescriptions
        const maxDaysSupply = Math.max(...this.eligiblePrescriptions.map(rx => rx.daySupply || 30));
        this.cycleDays = maxDaysSupply;
    }

    formatDate(date: string | Date | null): string {
        if (!date) return 'N/A';
        return moment(date).format('MM/DD/YYYY');
    }

    get isValid(): boolean {
        return !!(this.anchorDate && this.cycleDays && this.cycleDays > 0 && this.selectedPrescriptions.length > 1 && (this.selectedCardId || this.allowAutorefillWithoutPayment));
    }

    get allSelected(): boolean {
        return this.eligiblePrescriptions.length > 0 && this.selectedPrescriptions.length === this.eligiblePrescriptions.length;
    }

    get incompatiblePrescriptions(): EligiblePrescription[] {
        if (!this.cycleDays || this.selectedPrescriptions.length === 0) {
            return [];
        }

        // Get selected prescriptions
        const selectedRxList = this.eligiblePrescriptions.filter(rx =>
            this.selectedPrescriptions.includes(rx.rxID!)
        );

        // Check each prescription's day supply against cycle days
        return selectedRxList.filter(rx => {
            if (!rx.daySupply || rx.daySupply <= 0) {
                return false; // Skip if no day supply
            }

            // Calculate the factor: cycleDays / daySupply
            const factor = this.cycleDays! / rx.daySupply;

            // Check if it's a standard factor (1, 1.5, 2, 3)
            // Allow for small floating point differences
            const standardFactors = [1, 1.5, 2, 3];
            const isStandardFactor = standardFactors.some(sf =>
                Math.abs(factor - sf) < 0.01
            );

            // Also check if daySupply is a factor of cycleDays (e.g., 30 divides into 90)
            const isDivisible = this.cycleDays! % rx.daySupply === 0;

            // Compatible if it's a standard factor OR if it divides evenly
            return !isStandardFactor && !isDivisible;
        });
    }

    get showCycleCompatibilityWarning(): boolean {
        return this.incompatiblePrescriptions.length > 0;
    }

    toggleSelectAll() {
        if (this.allSelected) {
            // Deselect all
            this.selectedPrescriptions = [];
        } else {
            // Select all
            this.selectedPrescriptions = this.eligiblePrescriptions.map(rx => rx.rxID);
        }
    }

    async save() {
        if (!this.isValid) {
            this.errorMessage = this.getValidationError();
            return;
        }

        this.saving = true;
        this.errorMessage = '';

        try {
            // Get selected prescription objects
            const selectedRxList = this.eligiblePrescriptions.filter(rx => this.selectedPrescriptions.includes(rx.rxID!));

            // Create MedSync configuration
            const medSyncConfig = {
                patientId: this.patient.id,
                anchorDate: this.anchorDate,
                cycleDays: this.cycleDays,
                selectedPrescriptions: this.selectedPrescriptions,
                enableShortFill: this.enableShortFill,
                cardId: this.selectedCardId,
            };

            // Save MedSync configuration (backend handles subscription creation/cancellation)
            await axios.post('/api/medsync/enroll', medSyncConfig);

            this.$notification(NotificationOptions.success(`Successfully enrolled patient in MedSync with ${ this.selectedPrescriptions.length } medications`));

            // If short fill is enabled, open the Refill in Batch modal
            if (this.enableShortFill) {
                this.calculateShortFills(selectedRxList);
                this.showRefillBatchModal = true;
                this.$nextTick(() => {
                    (this.$refs.refillBatchModal as any).show();
                });
            } else {
                // Close the modal
                this.$emit('completed');
            }
        } catch (error: any) {
            console.error('Error enrolling in MedSync:', error);
            this.errorMessage = error.response?.data?.message || 'Failed to enroll in MedSync. Please try again.';
        } finally {
            this.saving = false;
        }
    }

    calculateShortFills(prescriptions: EligiblePrescription[]) {
        // Calculate which prescriptions need short fills to align with anchor date
        this.shortFillPrescriptions = prescriptions
            .filter(rx => {
                if (!rx.nextFillDate) return false;
                const nextFill = new Date(rx.nextFillDate);
                return nextFill < this.anchorDate!;
            })
            .map(rx => {
                // Calculate the adjusted days supply based on days between next fill date and anchor date
                const nextFill = new Date(rx.nextFillDate!);
                const anchorDate = new Date(this.anchorDate!);

                // Calculate days between the prescription's next fill date and the anchor date (alignment date)
                const daysBetween = Math.ceil((anchorDate.getTime() - nextFill.getTime()) / (1000 * 60 * 60 * 24));

                // Set the adjusted days supply to the number of days until the alignment date (anchor date)
                // This ensures the prescription will be filled with just enough supply to last until the anchor date
                return {
                    ...rx,
                    originalDaysSupply: rx.daySupply,
                    adjustedDaysSupply: daysBetween > 0 ? daysBetween : rx.daySupply
                };
            });
    }

    getValidationError(): string {
        if (!this.anchorDate) {
            return 'Please select an anchor date.';
        }
        if (!this.cycleDays || this.cycleDays <= 0) {
            return 'Please enter a valid cycle days value.';
        }
        if (this.selectedPrescriptions.length < 2) {
            return 'Please select at least two medications for MedSync enrollment.';
        }
        if (!this.selectedCardId && !this.allowAutorefillWithoutPayment) {
            return 'Please select a payment card for the autorefill subscriptions.';
        }
        return 'Please correct the validation errors.';
    }

    onShortFillCompleted() {
        this.showRefillBatchModal = false;
        this.$emit('completed');
    }

    cancel() {
        this.$emit('completed');
    }
}
</script>

<style scoped>
.med-sync-enrollment {
}

.med-sync-enrollment h5 {
    color: #333;
    margin-bottom: 10px;
}

::v-deep .el-input__prefix {
    display: flex;
    align-items: center;
    top: -3px;
}
</style>
